import React, { useEffect, useState } from 'react';
import { Boxes, AlertTriangle, Loader2 } from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { createIntentTemplateDraft } from '../../../services/api';

/**
 * Intent Profiles Catalog v1.0 — Phase 1E.2
 *
 * Author a new template in DRAFT state. Service-side this creates BOTH
 * the template row AND an empty v1 DRAFT version. The author then opens
 * the template detail slideout, edits the v1 matchers, and submits for
 * review.
 *
 * <p>Scope handling in Phase 1E.2: we only expose TENANT-scoped
 * authoring. The caller's tenantId is auto-resolved from the auth
 * session — there is no scope picker. Org / Customer / Platform-scoped
 * templates require Platform Admin and a different lifecycle (Phase 4).
 * This keeps the v1 surface unambiguous; PA can still author
 * non-TENANT-scoped via direct API until the FE picker lands.</p>
 *
 * Props:
 *   - isOpen     — bool
 *   - onClose    — callback
 *   - onCreated  — callback({templateId}) when create succeeds (parent
 *                  uses this to open the detail slideout for the new row)
 */
export function CreateIntentTemplateSlideout({ isOpen, onClose, onCreated }) {
  /* Scope auto-resolution: the BE controller (createDraft) auto-derives
   * customerId / orgId / tenantId from the actor's SecurityContext when
   * the FE sends null for TENANT scope. That means this slideout doesn't
   * need to chase session.customerId edge-cases — we send scopeLevel and
   * the BE fills in the rest from the JWT cid / orgId / tenantId claims.
   * If the actor's session genuinely lacks one (e.g., Platform Admin
   * who hasn't switched into a customer), the service-layer scope-shape
   * validator returns 400 with a clear message which we surface in
   * errorMsg below. */

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [riskTier, setRiskTier] = useState('MEDIUM');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Reset form on each open
  useEffect(() => {
    if (isOpen) {
      setName(''); setDisplayName(''); setDescription('');
      setCategory(''); setRiskTier('MEDIUM');
      setSubmitting(false); setErrorMsg(null);
    }
  }, [isOpen]);

  const canSubmit = !submitting &&
    name.trim() !== '' &&
    displayName.trim() !== '' &&
    description.trim() !== '';

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg(null);
    const res = await createIntentTemplateDraft({
      scopeLevel: 'TENANT',
      // null scope IDs → backend auto-resolves from the actor's session
      // (SecurityContext.resolveTenantId/orgId/customerId fall back to
      // JWT claims when explicit overrides are null).
      customerId: null,
      orgId: null,
      tenantId: null,
      name: name.trim(),
      displayName: displayName.trim(),
      description: description.trim(),
      riskTier,
      category: category.trim() || null,
    });
    setSubmitting(false);
    if (res?.ok === false) {
      setErrorMsg(res.error || 'Failed to create template');
      return;
    }
    const data = res?.data ?? res;
    if (onCreated && data?.id) onCreated({ templateId: data.id });
    onClose && onClose();
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={520}
      ariaLabel="Create intent template"
      header={
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--v2-border-default)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <Boxes size={12} />
            <span>New intent template</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--v2-text-primary)', marginTop: 6 }}>
            {displayName.trim() || 'Untitled template'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--v2-text-muted)', marginTop: 2 }}>
            Creates a tenant-scoped template + empty v1 DRAFT. Author then edits the v1 matchers and submits for review.
          </div>
        </div>
      }
      footer={
        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--v2-border-default)', background: 'var(--v2-bg-muted)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleCreate} disabled={!canSubmit}>
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : 'Create draft'}
          </Button>
        </div>
      }
    >
      <div style={{ padding: '20px 24px' }}>
        {errorMsg && (
          <InfoBanner kind="error">{errorMsg}</InfoBanner>
        )}

        <Field label="Internal name" hint="Stable identifier used by the binding API. Lowercase, no spaces (e.g. answer_billing).">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="answer_billing"
            disabled={submitting}
            style={inputStyle}
          />
        </Field>

        <Field label="Display name" hint="Human-readable label shown in the catalog UI.">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Answer billing questions"
            disabled={submitting}
            style={inputStyle}
          />
        </Field>

        <Field label="Description" hint="What is this intent for? Compliance reviewers will read this when approving.">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Allow the agent to look up subscription state and send billing-related email replies."
            disabled={submitting}
            rows={4}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
          />
        </Field>

        <Field label="Risk tier" hint="Surfaces in the audit row for reviewer sorting.">
          <select
            value={riskTier}
            onChange={(e) => setRiskTier(e.target.value)}
            disabled={submitting}
            style={inputStyle}
          >
            <option value="LOW">LOW</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="HIGH">HIGH</option>
            <option value="CRITICAL">CRITICAL</option>
          </select>
        </Field>

        <Field label="Category" hint="Optional. Free-form tag, e.g. billing, support, infra.">
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="(optional)"
            disabled={submitting}
            style={inputStyle}
          />
        </Field>
      </div>
    </Slideout>
  );
}

/* ── primitives ─────────────────────────────────────────────────── */
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '1px solid var(--v2-border-strong)', borderRadius: 6, background: 'var(--v2-bg-surface)',
  color: 'var(--v2-text-primary)', outline: 'none', fontFamily: 'inherit',
};

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--v2-text-secondary)',
        marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 4, lineHeight: 1.4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function InfoBanner({ kind, children }) {
  const styles = {
    error: { color: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)', icon: <AlertTriangle size={14} /> },
    warn:  { color: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)', icon: <AlertTriangle size={14} /> },
  };
  const s = styles[kind] || styles.warn;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 12px', marginBottom: 16,
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 6, fontSize: 12, lineHeight: 1.4,
    }}>
      <span style={{ flexShrink: 0, marginTop: 1 }}>{s.icon}</span>
      <span>{children}</span>
    </div>
  );
}
